import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { DataTable } from '@/components/ui/data-table';
import { Skeleton } from '@/components/ui/skeleton';
import { useToast } from '@/hooks/useToast';
import {
  useBOMs,
  useCreateBOM,
  useBOMComponents,
  useBOMCostBreakdown,
  useProductionOrders,
  useCreateProductionOrder,
  useStartProduction,
  useCompleteProduction,
  useProductionMaterials,
  useIssueMaterial,
} from '@/hooks/useAssembly';
import { useItems } from '@/hooks/useItems';
import { useLocations } from '@/hooks/useLocations';
import { ModalForm, type FormField } from '@/components/ui/modal-form';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Package,
  Plus,
  Search,
  Play,
  CheckCircle,
  Wrench,
  Layers,
  DollarSign,
  ArrowRight,
} from 'lucide-react';
import { format } from 'date-fns';

const bomStatusColors: Record<string, string> = {
  draft: 'bg-gray-100 text-gray-800',
  active: 'bg-green-100 text-green-800',
  inactive: 'bg-red-100 text-red-800',
};

const productionStatusColors: Record<string, string> = {
  planned: 'bg-gray-100 text-gray-800',
  released: 'bg-blue-100 text-blue-800',
  in_progress: 'bg-orange-100 text-orange-800',
  completed: 'bg-green-100 text-green-800',
  cancelled: 'bg-red-100 text-red-800',
};

export default function AssemblyPage() {
  const { toast } = useToast();
  const [activeTab, setActiveTab] = useState('bom');
  const [searchQuery, setSearchQuery] = useState('');
  const [isCreateBOMModalOpen, setIsCreateBOMModalOpen] = useState(false);
  const [isCreateProductionModalOpen, setIsCreateProductionModalOpen] = useState(false);
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState<any>(null);
  const [selectedBOM, setSelectedBOM] = useState<any>(null);
  const [selectedProduction, setSelectedProduction] = useState<any>(null);

  const { data: boms = [], isLoading: bomsLoading } = useBOMs({ active_only: false });
  const { data: productions = [], isLoading: productionsLoading } = useProductionOrders();
  const { data: itemsData } = useItems({ limit: 100 });
  const { data: locations = [] } = useLocations();

  const { data: bomComponents } = useBOMComponents(selectedBOM?.bom_id);
  const { data: bomCostBreakdown } = useBOMCostBreakdown(selectedBOM?.bom_id);
  const { data: productionMaterials } = useProductionMaterials(selectedProduction?.production_id);

  const createBOMMutation = useCreateBOM();
  const createProductionMutation = useCreateProductionOrder();
  const startProductionMutation = useStartProduction();
  const completeProductionMutation = useCompleteProduction();
  const issueMaterialMutation = useIssueMaterial();

  const items = itemsData?.data || [];

  const createBOMFields: FormField[] = [
    { name: 'bom_code', label: 'BOM Code', type: 'text', required: true },
    { name: 'name', label: 'Name', type: 'text', required: true },
    { name: 'description', label: 'Description', type: 'textarea' },
    { name: 'finished_item_id', label: 'Finished Item', type: 'select', required: true,
      options: items.map(i => ({ value: i.item_id, label: `${i.sku} - ${i.name}` })) },
    { name: 'bom_type', label: 'Type', type: 'select', required: true,
      options: [
        { value: 'manufacturing', label: 'Manufacturing' },
        { value: 'kitting', label: 'Kitting' },
        { value: 'repair', label: 'Repair' },
      ]},
    { name: 'work_center', label: 'Work Center', type: 'text' },
    { name: 'lead_time_hours', label: 'Lead Time (Hours)', type: 'number' },
    { name: 'batch_size', label: 'Batch Size', type: 'number' },
  ];

  const createProductionFields: FormField[] = [
    { name: 'bom_id', label: 'BOM', type: 'select', required: true,
      options: boms.filter((b: any) => b.is_active).map(b => ({ value: b.bom_id, label: `${b.bom_code} - ${b.name}` })) },
    { name: 'quantity_planned', label: 'Quantity to Produce', type: 'number', required: true },
    { name: 'location_id', label: 'Production Location', type: 'select',
      options: locations.map(l => ({ value: l.location_id, label: l.name })) },
    { name: 'planned_start_date', label: 'Planned Start', type: 'datetime-local' },
    { name: 'planned_end_date', label: 'Planned End', type: 'datetime-local' },
    { name: 'priority', label: 'Priority (1-10)', type: 'number' },
    { name: 'notes', label: 'Notes', type: 'textarea' },
  ];

  const handleCreateBOM = async (data: any) => {
    try {
      await createBOMMutation.mutateAsync(data);
      toast({ title: 'BOM created successfully' });
      setIsCreateBOMModalOpen(false);
    } catch (error: any) {
      toast({ title: 'Error', description: error.message, variant: 'destructive' });
    }
  };

  const handleCreateProduction = async (data: any) => {
    try {
      await createProductionMutation.mutateAsync(data);
      toast({ title: 'Production order created' });
      setIsCreateProductionModalOpen(false);
    } catch (error: any) {
      toast({ title: 'Error', description: error.message, variant: 'destructive' });
    }
  };

  const handleStartProduction = async (productionId: string) => {
    try {
      await startProductionMutation.mutateAsync(productionId);
      toast({ title: 'Production started' });
    } catch (error: any) {
      toast({ title: 'Error', description: error.message, variant: 'destructive' });
    }
  };

  const handleCompleteProduction = async (productionId: string) => {
    try {
      await completeProductionMutation.mutateAsync({
        productionId,
        quantityProduced: selectedProduction?.quantity_planned,
      });
      toast({ title: 'Production completed' });
      setIsDetailModalOpen(false);
    } catch (error: any) {
      toast({ title: 'Error', description: error.message, variant: 'destructive' });
    }
  };

  const bomColumns = [
    { accessorKey: 'bom_code', header: 'BOM Code' },
    { accessorKey: 'name', header: 'Name' },
    { accessorKey: 'finished_item_name', header: 'Finished Item' },
    {
      accessorKey: 'bom_type',
      header: 'Type',
      cell: ({ row }: any) => (
        <Badge variant="outline">{row.bom_type}</Badge>
      ),
    },
    {
      accessorKey: 'estimated_cost',
      header: 'Est. Cost',
      cell: ({ row }: any) => row.estimated_cost 
        ? `$${Number(row.estimated_cost).toLocaleString()}`
        : '-',
    },
    {
      accessorKey: 'is_active',
      header: 'Status',
      cell: ({ row }: any) => (
        <Badge className={row.is_active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}>
          {row.is_active ? 'Active' : 'Inactive'}
        </Badge>
      ),
    },
  ];

  const productionColumns = [
    { accessorKey: 'production_number', header: 'WO #' },
    { accessorKey: 'bom_name', header: 'BOM' },
    { accessorKey: 'finished_item_name', header: 'Product' },
    {
      accessorKey: 'quantity_planned',
      header: 'Qty',
      cell: ({ row }: any) => `${row.quantity_produced || 0}/${row.quantity_planned}`,
    },
    {
      accessorKey: 'status',
      header: 'Status',
      cell: ({ row }: any) => (
        <Badge className={productionStatusColors[row.status]}>
          {row.status.replace('_', ' ')}
        </Badge>
      ),
    },
    {
      accessorKey: 'priority',
      header: 'Priority',
      cell: ({ row }: any) => (
        <span className={row.priority >= 8 ? 'text-red-600 font-bold' : ''}>
          {row.priority}
        </span>
      ),
    },
    {
      id: 'actions',
      header: 'Actions',
      cell: ({ row }: any) => (
        <div className="flex gap-2">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => {
              setSelectedProduction(row);
              setIsDetailModalOpen(true);
            }}
          >
            View
          </Button>
          {row.status === 'planned' && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => handleStartProduction(row.production_id)}
            >
              <Play className="h-4 w-4" />
            </Button>
          )}
        </div>
      ),
    },
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Assembly & BOM</h1>
          <p className="text-muted-foreground">Bill of Materials and Production Orders</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => setIsCreateBOMModalOpen(true)}>
            <Plus className="h-4 w-4 mr-2" />
            New BOM
          </Button>
          <Button onClick={() => setIsCreateProductionModalOpen(true)}>
            <Wrench className="h-4 w-4 mr-2" />
            New Work Order
          </Button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-blue-100 rounded-lg">
                <Layers className="h-6 w-6 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Active BOMs</p>
                <p className="text-2xl font-bold">
                  {boms.filter((b: any) => b.is_active).length}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-orange-100 rounded-lg">
                <Wrench className="h-6 w-6 text-orange-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">In Progress</p>
                <p className="text-2xl font-bold">
                  {productions.filter((p: any) => p.status === 'in_progress').length}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-green-100 rounded-lg">
                <CheckCircle className="h-6 w-6 text-green-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Completed</p>
                <p className="text-2xl font-bold">
                  {productions.filter((p: any) => p.status === 'completed').length}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-purple-100 rounded-lg">
                <DollarSign className="h-6 w-6 text-purple-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Planned Value</p>
                <p className="text-2xl font-bold">
                  ${productions.reduce((sum: number, p: any) => sum + (p.estimated_cost || 0), 0).toLocaleString()}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="bom">Bill of Materials</TabsTrigger>
          <TabsTrigger value="production">Production Orders</TabsTrigger>
        </TabsList>

        <TabsContent value="bom" className="space-y-4">
          <Card>
            <CardContent className="pt-6">
              {bomsLoading ? (
                <div className="space-y-4">
                  {[1, 2, 3, 4, 5].map((i) => (
                    <Skeleton key={i} className="h-12 w-full" />
                  ))}
                </div>
              ) : (
                <DataTable columns={bomColumns} data={boms} />
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="production" className="space-y-4">
          <Card>
            <CardContent className="pt-6">
              {productionsLoading ? (
                <div className="space-y-4">
                  {[1, 2, 3, 4, 5].map((i) => (
                    <Skeleton key={i} className="h-12 w-full" />
                  ))}
                </div>
              ) : (
                <DataTable columns={productionColumns} data={productions} />
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Create BOM Modal */}
      <ModalForm
        isOpen={isCreateBOMModalOpen}
        onClose={() => setIsCreateBOMModalOpen(false)}
        onSubmit={handleCreateBOM}
        fields={createBOMFields}
        title="Create Bill of Materials"
        isLoading={createBOMMutation.isPending}
      />

      {/* Create Production Modal */}
      <ModalForm
        isOpen={isCreateProductionModalOpen}
        onClose={() => setIsCreateProductionModalOpen(false)}
        onSubmit={handleCreateProduction}
        fields={createProductionFields}
        title="Create Production Order"
        isLoading={createProductionMutation.isPending}
      />

      {/* Detail Modal */}
      <Dialog open={isDetailModalOpen} onOpenChange={setIsDetailModalOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {selectedProduction?.production_number} - {selectedProduction?.finished_item_name}
            </DialogTitle>
          </DialogHeader>
          
          {selectedProduction && (
            <div className="space-y-6">
              {/* Status */}
              <div className="flex items-center gap-4">
                <Badge className={productionStatusColors[selectedProduction.status]}>
                  {selectedProduction.status.replace('_', ' ')}
                </Badge>
                <span className="text-muted-foreground">
                  Progress: {selectedProduction.quantity_produced || 0} / {selectedProduction.quantity_planned}
                </span>
              </div>

              {/* BOM Components */}
              {selectedProduction.status !== 'completed' && (
                <div>
                  <h3 className="font-semibold mb-2">Material Requirements</h3>
                  <DataTable
                    columns={[
                      { accessorKey: 'item_name', header: 'Component' },
                      { accessorKey: 'item_code', header: 'Code' },
                      { accessorKey: 'quantity_required', header: 'Required' },
                      { accessorKey: 'quantity_consumed', header: 'Issued' },
                      {
                        accessorKey: 'status',
                        header: 'Status',
                        cell: ({ row }: any) => (
                          <Badge className={row.status === 'issued' ? 'bg-green-100 text-green-800' : 'bg-gray-100'}>
                            {row.status}
                          </Badge>
                        ),
                      },
                      {
                        id: 'actions',
                        header: '',
                        cell: ({ row }: any) => (
                          row.status !== 'issued' && selectedProduction.status === 'in_progress' && (
                            <Button size="sm" variant="outline"
                              onClick={() => issueMaterialMutation.mutate({
                                productionId: selectedProduction.production_id,
                                itemId: row.item_id,
                                quantity: row.quantity_required - (row.quantity_consumed || 0),
                              })}
                            >
                              Issue
                            </Button>
                          )
                        ),
                      },
                    ]}
                    data={productionMaterials || []}
                  />
                </div>
              )}

              {/* Actions */}
              {selectedProduction.status === 'in_progress' && (
                <Button onClick={() => handleCompleteProduction(selectedProduction.production_id)} className="w-full">
                  <CheckCircle className="h-4 w-4 mr-2" />
                  Complete Production
                </Button>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
